import { DateInput, DatePicker } from "@amzn/awsui-components-react";
import FormFieldWrapper from "./FormFieldWrapper/FormFieldWrapper";
import { CustomDateFormFieldProps } from "interfaces";
import { trackCustomEvent, useTrackerId } from "@amzn/uxm-aws-ui-components-react/lib/src/uxm/custom-event";

const DATE_INPUT_PLACEHOLDER = "YYYY/MM/DD";

/**
 * A FormField that provides a date input
 *
 * @param {string} label - The form field label
 * @param {string} value - The current input value
 * @param {function} setValue - Function to update the current input value
 * @param {string} errorText - Error message to display
 * @param {string} warningText - Warning message to display
 * @param {string} description - Description text for the field
 * @param {React.ReactNode} info - Additional info to display
 * @param {string} placeholder - Placeholder text for the date input (defaults to YYYY/MM/DD)
 * @param {boolean} disabled - Whether the input is disabled (defaults to false)
 * @param {string} [testId] - Test identifier for the component
 *
 * @returns {JSX.Element} A FormField component with a DateInput
 */
const CustomDateFormField = ({
    label,
    value,
    setValue,
    errorText,
    warningText,
    description,
    info,
    placeholder = DATE_INPUT_PLACEHOLDER,
    disabled = false,
    variant = "date",
    testId,
    touchpoint,
}: CustomDateFormFieldProps) => {
    let selectedInput: React.ReactNode;

    const trackerId = useTrackerId();

    /**
     * Track custom uxm event for date change
     */
    const onChange = ({ detail }: { detail: { value: string } }) => {
        setValue(detail.value);

        const eventData: Record<string, unknown> = {
            action: "date-change",
            date: detail.value,
        };

        trackCustomEvent(eventData, touchpoint, trackerId);
    };

    switch (variant) {
        case "date":
            selectedInput = (
                <DateInput onChange={onChange} value={value} placeholder={placeholder} disabled={disabled} />
            );
            break;
        case "date-picker":
        default:
            selectedInput = (
                <DatePicker onChange={onChange} value={value} placeholder={placeholder} disabled={disabled} />
            );
    }

    return (
        <FormFieldWrapper
            testId={testId}
            label={label}
            errorText={errorText}
            warningText={warningText}
            description={description}
            info={info}
            input={selectedInput}
        />
    );
};

export default CustomDateFormField;
